/**
 * Member Referral Card — /refer-card
 *
 * A printable/shareable card any logged-in member can generate. QR encodes
 * their personal referral link (from GET /api/referrals/my-code) so scanning
 * it gives the friend a signup path attributed to this member.
 *
 * Reuses BusinessCardFront layout so the aesthetic stays consistent.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toPng } from 'html-to-image';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import {
  Download,
  Loader2,
  Copy,
  QrCode,
  Share2,
  Info,
} from 'lucide-react';
import { BusinessCardFront } from '../components/business-card/BusinessCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReferCard() {
  const { user } = useAuth();
  const cardRef = useRef(null);
  const [referralCode, setReferralCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const [form, setForm] = useState({
    tagline: 'Come join my dog club',
    petName: '',
  });
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/referrals/my-code`);
        if (!cancel) setReferralCode(res.data);
      } catch {
        /* handled by shareLink fallback below */
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const shareLink = useMemo(() => {
    const base = window.location.origin;
    if (referralCode?.shareable_link) return `${base}${referralCode.shareable_link}`;
    // Fallback: send them to the site with the username tag
    if (user?.username) return `${base}/?ref=${user.username}`;
    return base;
  }, [referralCode, user]);

  const cardProps = {
    fullName: user?.display_name || user?.full_name || user?.username || 'A club member',
    title: 'Club Member',
    email: user?.email || '',
    phone: '',
    website: 'americandogclub.com',
    tagline: form.tagline,
    orgName: 'American Dog Club',
    logoSrc: '/branding/princess/princess-mark-180.png',
    petPhotoSrc: user?.avatar_url || '/branding/princess/princess-portrait-256.png',
    petName: form.petName,
    qrTarget: shareLink,
  };

  const download = async () => {
    if (!cardRef.current) return;
    setExporting(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: '#ffffff',
      });
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `${(user?.username || 'member')}-referral-card.png`;
      a.click();
      toast.success('Downloaded');
    } catch (err) {
      toast.error('Export failed. Try again.');
    } finally {
      setExporting(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      toast.success('Link copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join me at the American Dog Club',
          text: 'Come join my dog club — first year is free with my link.',
          url: shareLink,
        });
      } catch {
        /* user cancelled */
      }
    } else {
      copyLink();
    }
  };

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="refer-card-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
          <QrCode className="h-7 w-7 text-amber-500" />
          Your invite card
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Print or share this card. The QR opens your personal referral link — friends who join with it are attributed to you.
        </p>
      </div>

      <div className="grid lg:grid-cols-[380px_1fr] gap-6">
        {/* Controls */}
        <Card data-testid="refer-card-controls">
          <CardContent className="p-5 space-y-4">
            <div>
              <Label htmlFor="tagline" className="text-xs">Tagline (short, personal)</Label>
              <Input
                id="tagline"
                value={form.tagline}
                onChange={upd('tagline')}
                maxLength={60}
                data-testid="refer-tagline"
              />
            </div>
            <div>
              <Label htmlFor="petName" className="text-xs">Your dog's name (optional caption)</Label>
              <Input
                id="petName"
                value={form.petName}
                onChange={upd('petName')}
                maxLength={40}
                data-testid="refer-petname"
              />
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs text-amber-900 font-medium mb-1 flex items-center gap-1.5">
                <Info className="h-3.5 w-3.5" />
                Your invite link
              </p>
              <p className="text-xs font-mono text-slate-700 break-all mb-2">{shareLink}</p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={copyLink}
                  data-testid="refer-copy-link"
                >
                  <Copy className="mr-1 h-3.5 w-3.5" />
                  Copy
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={shareNative}
                  data-testid="refer-share-native"
                >
                  <Share2 className="mr-1 h-3.5 w-3.5" />
                  Share
                </Button>
              </div>
            </div>

            <Button
              onClick={download}
              disabled={exporting}
              className="w-full bg-amber-500 hover:bg-amber-600"
              data-testid="refer-download"
            >
              {exporting ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Rendering…</>
              ) : (
                <><Download className="mr-2 h-4 w-4" />Download PNG (print-ready)</>
              )}
            </Button>
            <p className="text-[11px] text-muted-foreground text-center">
              1050×600 @ 300 DPI · US business-card size
            </p>
          </CardContent>
        </Card>

        {/* Preview */}
        <Card>
          <CardContent className="p-5">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
              Preview (50%)
            </p>
            <div
              className="mx-auto"
              style={{ width: 525, maxWidth: '100%' }}
              data-testid="refer-preview"
            >
              <div
                style={{
                  width: 1050,
                  transform: 'scale(0.5)',
                  transformOrigin: 'top left',
                  height: 300,
                  overflow: 'visible',
                }}
              >
                <BusinessCardFront ref={cardRef} {...cardProps} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
